import { createContext, ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { staticResources } from '../i18n';
import { subscribeToContentEntries } from '../services/content';
import type { ContentEntry } from '../types/content';

type ContentMap = Record<string, ContentEntry>;
type ContentContextValue = {
  getValue: <T extends string | string[]>(key: string, defaultValue: T) => T;
  entries: ContentEntry[];
};

type TranslationBundle = Record<string, unknown>;

const normalizeLocaleCode = (value: string) => value.split('-')[0]?.toLowerCase() ?? value;

const setNestedValue = (target: TranslationBundle, path: string, value: string | string[]) => {
  const segments = path.split('.');
  let current: TranslationBundle = target;

  segments.forEach((segment, index) => {
    const isLast = index === segments.length - 1;
    if (isLast) {
      current[segment] = value;
      return;
    }

    const existing = current[segment];

    if (typeof existing === 'object' && existing !== null && !Array.isArray(existing)) {
      current = existing as TranslationBundle;
    } else {
      const nextLevel: TranslationBundle = {};
      current[segment] = nextLevel;
      current = nextLevel;
    }
  });
};
const ContentContext = createContext<ContentContextValue | undefined>(undefined);

const normalizeList = (value: string | string[]): string[] => {
  if (Array.isArray(value)) {
    return value;
  }
  return value
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
};

const normalizeText = (value: string | string[]): string => {
  if (Array.isArray(value)) {
    return value.join('\n');
  }
  return value;
};

const mapEntries = (entries: ContentEntry[]): ContentMap => {
  const map: ContentMap = {};
  entries.forEach((entry) => {
    map[entry.contentKey] = entry;
  });
  return map;
};

interface ContentProviderProps {
  children: ReactNode;
}

export const ContentProvider = ({ children }: ContentProviderProps) => {
  const { i18n: i18nInstance } = useTranslation();
  const [entries, setEntries] = useState<ContentEntry[]>([]);

  useEffect(() => {
    const locale = normalizeLocaleCode(i18nInstance.language);
    const unsubscribe = subscribeToContentEntries(
      locale,
      (items) => {
        console.log('[ContentContext] Loaded entries for locale:', locale, items);
        console.log('[ContentContext] Firebase entries loaded for', locale, ':', items.filter(e => e.contentKey.startsWith('value.media')));
        setEntries(items);
      },
      (error) => {
        console.warn('Content subscription error', error);
      },
    );
    return () => {
      unsubscribe();
    };
  }, [i18nInstance.language]);

  useEffect(() => {
    const languageTag = i18nInstance.language;
    const normalizedLocale = normalizeLocaleCode(languageTag);
    const baseBundle = staticResources[normalizedLocale as keyof typeof staticResources]?.translation;

    console.log('[ContentContext] Reloading resources for', languageTag);
    console.log('[ContentContext] normalizedLocale:', normalizedLocale);
    console.log('[ContentContext] staticResources keys:', Object.keys(staticResources));
    console.log('[ContentContext] baseBundle exists:', !!baseBundle);
    console.log('[ContentContext] baseBundle has value:', !!(baseBundle as any)?.value);
    console.log('[ContentContext] baseBundle.value.media:', (baseBundle as any)?.value?.media);

    if (baseBundle) {
      if (i18nInstance.hasResourceBundle(languageTag, 'translation')) {
        i18nInstance.removeResourceBundle(languageTag, 'translation');
      }
      i18nInstance.addResourceBundle(languageTag, 'translation', baseBundle, true, true);
      console.log('[ContentContext] Added baseBundle, checking result...');
      const checkBundle = i18nInstance.getResourceBundle(languageTag, 'translation');
      console.log('[ContentContext] After adding - bundle has value:', !!(checkBundle as any)?.value);

      if (languageTag !== normalizedLocale) {
        i18nInstance.addResourceBundle(normalizedLocale, 'translation', baseBundle, true, true);
      }
    }

    if (entries.length > 0) {
      const dynamicBundle: TranslationBundle = {};
      entries.forEach((entry) => {
        setNestedValue(dynamicBundle, entry.contentKey, entry.value);
      });
      i18nInstance.addResourceBundle(languageTag, 'translation', dynamicBundle, true, true);

      if (languageTag !== normalizedLocale) {
        i18nInstance.addResourceBundle(normalizedLocale, 'translation', dynamicBundle, true, true);
      }
    }

    // Notify react-i18next consumers that resources for the current namespace have changed
    i18nInstance.emit('loaded', { lng: languageTag, ns: 'translation' });
    i18nInstance.emit('languageChanged', languageTag);
  }, [entries, i18nInstance]);

  const entryMap = useMemo(() => mapEntries(entries), [entries]);

  const getValue = useMemo(
    () =>
      (<T extends string | string[]>(key: string, defaultValue: T): T => {
        const entry = entryMap[key];
        if (!entry) {
          return defaultValue;
        }

        if (Array.isArray(defaultValue)) {
          const listValue = normalizeList(entry.value);
          return listValue as T;
        }

        const textValue = normalizeText(entry.value);
        return textValue as T;
      }),
    [entryMap],
  );

  const value: ContentContextValue = useMemo(
    () => ({
      getValue,
      entries,
    }),
    [entries, getValue],
  );

  return <ContentContext.Provider value={value}>{children}</ContentContext.Provider>;
};

export const useContent = () => {
  const context = useContext(ContentContext);
  if (!context) {
    throw new Error('useContent must be used within a ContentProvider');
  }
  return context;
};
